import { InjectionToken } from "@angular/core";

export interface IntervalRunner {
  clear(): void;
}

export type IntervalRunnerFactory = (
  intervalMs: number,
  callback: () => void,
) => IntervalRunner;

export const INTERVAL_RUNNER_FACTORY =
  new InjectionToken<IntervalRunnerFactory>("IntervalRunnerFactory");

// Default implementation using JavaScript `setInterval`
export const createDefaultIntervalRunner: IntervalRunnerFactory = (
  intervalMs: number,
  callback: () => void,
): IntervalRunner => new DefaultIntervalRunner(intervalMs, callback);

export class DefaultIntervalRunner implements IntervalRunner {
  private readonly intervalId: ReturnType<typeof setInterval>;

  constructor(intervalMs: number, callback: () => void) {
    this.intervalId = setInterval(callback, intervalMs);
  }

  public clear(): void {
    clearInterval(this.intervalId);
  }
}

// Mock implementation for tests
export class MockIntervalManager {
  private runners: MockIntervalRunner[] = [];

  public getFactory(): IntervalRunnerFactory {
    return (_intervalMs: number, callback: () => void) => {
      const runner = new MockIntervalRunner(this, callback);
      this.runners.push(runner);

      return runner;
    };
  }

  public clear(runner: MockIntervalRunner): void {
    this.runners = this.runners.filter((r) => r !== runner);
  }

  public async tick(): Promise<void> {
    await Promise.all(this.runners.map((runner) => runner.run()));
  }
}

export class MockIntervalRunner implements IntervalRunner {
  constructor(
    private readonly manager: MockIntervalManager,
    private readonly callback: () => void,
  ) {}

  public async run(): Promise<void> {
    return this.callback();
  }

  public clear(): void {
    this.manager.clear(this);
  }
}
